import { Checkbox as BaseCheckbox } from '@base-ui-components/react/checkbox';
import { cn } from '../cn.js';
import { IconCheck, IconPartial } from '../icons.js';

export interface CheckboxProps extends BaseCheckbox.Root.Props {
  /** Marks the control invalid and links it to its message; FormField wires this for you. */
  invalid?: boolean;
}

/**
 * A checkbox.
 *
 * Base UI owns the keyboard, the ARIA and the indeterminate state; this owns how it looks. The
 * box keeps its border when checked rather than becoming a plain filled square, so the control is
 * still legible against the accent at a glance — and a tenant may replace the accent, so the
 * shape has to carry the state as well as the colour does.
 */
export function Checkbox({ className, invalid = false, ...props }: CheckboxProps) {
  return (
    <BaseCheckbox.Root
      aria-invalid={invalid || undefined}
      className={cn(
        'inline-flex size-(--size-icon-md) shrink-0 cursor-pointer items-center justify-center',
        'rounded-(--radius-sm) border bg-(--color-surface)',
        invalid ? 'border-(--color-danger)' : 'border-(--color-border-strong)',
        'transition-colors duration-(--motion-duration-fast) motion-reduce:transition-none',
        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-surface)',
        'data-[checked]:border-(--color-accent) data-[checked]:bg-(--color-accent)',
        'data-[indeterminate]:border-(--color-accent) data-[indeterminate]:bg-(--color-accent)',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-(--opacity-disabled)',
        className,
      )}
      {...props}
    >
      <BaseCheckbox.Indicator
        className="flex text-(--color-accent-foreground) data-[unchecked]:hidden"
        render={(indicatorProps, state) => (
          <span {...indicatorProps}>
            {state.indeterminate ? (
              <IconPartial aria-hidden="true" className="size-(--size-icon-sm)" />
            ) : (
              <IconCheck aria-hidden="true" className="size-(--size-icon-sm)" />
            )}
          </span>
        )}
      />
    </BaseCheckbox.Root>
  );
}
